import type { CourseSkillLevel } from "@/lib/constants";
import { handleSupabaseList, supabase } from "@/lib/supabase-unified";
import { lessonThumbnailProgressPercent } from "@/utils/lessonProgressPercent";

export type GlobalSearchItemType = "course" | "lesson" | "collection";

export type GlobalSearchRpcRow = {
  id: number;
  type: string;
  title: string;
  short_title?: string | null;
  summary: string | null;
  thumbnail: string | null;
  slug: string;
  course_slug: string | null;
  course_title: string | null;
  week_number?: number | null;
  main_work?: number | null;
  main_work_title?: string | null;
  published_at: string | null;
  skill_level: CourseSkillLevel | null;
  user_progress?: number | null;
  no_of_videos?: number | null;
  completed_video_count?: number | null;
  video_count?: number | null;
};

export type GlobalSearchItem = {
  id: number;
  type: GlobalSearchItemType;
  title: string;
  short_title?: string | null;
  summary: string;
  thumbnailUrl: string | null;
  slug: string;
  courseSlug?: string | null;
  courseTitle?: string | null;
  week_number?: number | null;
  main_work?: number | null;
  main_work_title?: string | null;
  publishedAt?: string | null;
  skillLevel: CourseSkillLevel | null;
  progressPercentage: number;
};

export function globalSearchProgressPercentage(r: GlobalSearchRpcRow): number {
  return lessonThumbnailProgressPercent({
    userProgress: r.user_progress,
    videoCount: r.video_count,
    noOfVideos: r.no_of_videos,
    completedVideoCount: r.completed_video_count,
  });
}

function mapRpcRowToItem(r: GlobalSearchRpcRow): GlobalSearchItem {
  const t = r.type === "course" ? "course" : r.type === "collection" ? "collection" : "lesson";
  return {
    id: r.id,
    type: t,
    title: r.title,
    short_title: r.short_title ?? null,
    summary: r.summary ?? "",
    thumbnailUrl: r.thumbnail ?? null,
    slug: r.slug,
    courseSlug: r.course_slug ?? null,
    courseTitle: r.course_title ?? null,
    week_number: r.week_number ?? null,
    main_work: r.main_work ?? null,
    main_work_title: r.main_work_title ?? null,
    publishedAt: r.published_at ?? null,
    skillLevel: r.skill_level ?? null,
    progressPercentage: globalSearchProgressPercentage(r),
  };
}

export async function searchLessonsAndCourses(
  rawQuery: string,
  limit: number = 20,
  userId?: string | null,
  skillLevels?: CourseSkillLevel[] | null,
  // Minimum status to surface. Defaults to Published(3); allowlisted draft
  // viewers pass Retired(0) to also find non-published courses/lessons. The RPC
  // already accepts these params (defaulting to 3), so non-viewers are unchanged.
  minStatus: number = 3,
): Promise<GlobalSearchItem[]> {
  const query = rawQuery.trim();
  if (!query) return [];

  const levels =
    skillLevels?.length && skillLevels.length > 0 ? skillLevels : null;

  const rows = await handleSupabaseList<GlobalSearchRpcRow>(
    supabase.rpc("global_search_v11", {
      search_term: query,
      p_limit: limit,
      p_user_id: userId ?? null,
      p_skill_levels: levels,
      p_min_status_course: minStatus,
      p_min_status_lesson: minStatus,
    }) as never,
  );

  if (!rows.length) return [];

  return rows.map(mapRpcRowToItem);
}

